import {
  LABEEB_CONFIG,
  LABEEB_ENVIRONMENT_SETTINGS,
  LABEEB_TENANTS,
  LABEEB_TIERS,
  labeebPaged,
  type LabeebConfigInfo,
  type LabeebEnvironmentSettings,
  type LabeebTenantSummary,
  type LabeebTierRow,
} from "../../api/labeeb";
import { metricPath, type FleetMetricEnvelope, type McpQuotaAll, type McpTenantList } from "../../api/mcp";
import { MetricTile } from "../../components/charts";
import { retryOf, statOf } from "../../components/states";
import { Page } from "../../components/ui";
import type { AppConfig } from "../../config";
import { useAuditFeed } from "../../hooks/useAuditFeed";
import { labeebPage, usePagedList } from "../../hooks/usePagedList";
import { useResource } from "../../hooks/useResource";
import { ProductCoverage } from "../../products/ProductCoverage";
import { ProductTag } from "../../products/PerProduct";
import { DecisionPanel } from "./panels/Decisions";
import { LabeebStrip } from "./panels/LabeebStrip";
import { QuotaOverridesByTier } from "./panels/QuotaOverridesByTier";
import { RecentChanges } from "./panels/RecentChanges";
import { TierAndCredits } from "./panels/TierAndCredits";
import { TopToolsToday } from "./panels/TopToolsToday";

/**
 * Fleet posture — both products, and only fields a documented route returns.
 *
 * MCP: credits and admin actions today, tenants, the tier distribution, credit
 * posture (`tiers[t].tool_default`), top tools, cap overrides.
 * Labeeb: the first page of businesses, suspensions on those rows, tiers, the
 * default tier, and the domains and plugins disabled fleet-wide.
 *
 * The design also drew a Messages tile, a computed-cost tile and a cost-gap
 * chart. Neither product serves any of them, so they are not drawn.
 */
export function FleetPosture({ config }: { config: AppConfig }) {
  const tenants = useResource<McpTenantList>(config, "/mcp/admin/tenants");
  const quota = useResource<McpQuotaAll>(config, "/mcp/admin/quota");
  // Seven independent calls, not one bundled read: `GET /admin/fleet/metrics`
  // is gone (MCP split it into `GET /admin/metrics/{key}`, one metric per
  // call, no shared `generated_at` because there is no longer one moment that
  // produces all of them together). Each metric gets its own resource so one
  // failing tile does not blank the other six.
  const usageToday = useResource<FleetMetricEnvelope<"usage_today">>(
    config,
    metricPath("usage_today"),
  );
  const tierDistribution = useResource<FleetMetricEnvelope<"tier_distribution">>(
    config,
    metricPath("tier_distribution"),
  );
  const adminActionsToday = useResource<FleetMetricEnvelope<"admin_actions_today">>(
    config,
    metricPath("admin_actions_today"),
  );
  const tenantsCount = useResource<FleetMetricEnvelope<"tenants_count">>(
    config,
    metricPath("tenants_count"),
  );
  const topToolsToday = useResource<FleetMetricEnvelope<"top_tools_today">>(
    config,
    metricPath("top_tools_today"),
  );
  const quotaOverridesCount = useResource<FleetMetricEnvelope<"quota_overrides_count">>(
    config,
    metricPath("quota_overrides_count"),
  );
  // First page only. A `next_cursor` means the count is partial — the tile
  // says so instead of printing the page length as the size of the fleet.
  const labeebBusinesses = usePagedList<LabeebTenantSummary>(
    config,
    labeebPaged(LABEEB_TENANTS),
    labeebPage,
  );
  const labeebTiers = usePagedList<LabeebTierRow>(config, labeebPaged(LABEEB_TIERS), labeebPage);
  const labeebConfig = useResource<LabeebConfigInfo>(config, LABEEB_CONFIG);
  const labeebEnv = useResource<LabeebEnvironmentSettings>(config, LABEEB_ENVIRONMENT_SETTINGS);
  // Each product's own audit tail. Merged below by time; a failure of one
  // stays a notice and does not blank the other.
  const mcpAudit = useAuditFeed(config, "");
  const labeebAudit = useAuditFeed(config, "", "labeeb");

  // "…" only while the read is in flight. A failure says so — the old tiles
  // treated a 503 as "still loading" and an operator waited on a number that
  // was never going to arrive.
  const usageFigure = statOf(usageToday.state, (env) =>
    typeof env.value?.total_credits === "number" ? env.value.total_credits.toLocaleString() : null,
  );
  const actionsFigure = statOf(adminActionsToday.state, (env) =>
    typeof env.value === "number" ? env.value.toLocaleString() : null,
  );
  const tenantFigure = statOf(tenantsCount.state, (env) =>
    typeof env.value === "number" ? env.value.toLocaleString() : null,
  );
  const usageDay =
    usageToday.state.kind === "ready" && typeof usageToday.state.data.value?.total_credits === "number"
      ? usageToday.state.data.value.day
      : undefined;

  return (
    <Page
      title="Fleet posture"
      subtitle={`${config.env === "prod" ? "Production" : "Development"} · where the fleet stands and what needs a decision.`}
    >
      <div className="flex flex-col gap-6">
        <ProductCoverage config={config} />

        {/* ── MCP headline strip ──────────────────────────────────────── */}
        <div className="grid gap-4 [grid-template-columns:repeat(auto-fit,minmax(185px,1fr))]">
          <MetricTile
            label="Credits today"
            aside={<ProductTag id="mcp" />}
            value={String(usageFigure.value)}
            note={usageFigure.hint ?? "across the fleet"}
            onRetry={retryOf(usageToday.state.kind, usageToday.reload)}
            detail="One UTC day — this route serves no history, so there is no trend to draw."
            from={usageDay ?? "—"}
            to="UTC"
          />

          <MetricTile
            label="Admin changes today"
            aside={<ProductTag id="mcp" />}
            value={String(actionsFigure.value)}
            note={actionsFigure.hint ?? "through this console"}
            onRetry={retryOf(adminActionsToday.state.kind, adminActionsToday.reload)}
            detail="A UTC calendar day, not a rolling 24 hours — it resets at midnight."
            from={usageDay ?? "—"}
            to="UTC"
          />

          <MetricTile
            label="Tenants"
            aside={<ProductTag id="mcp" />}
            value={String(tenantFigure.value)}
            note={tenantFigure.hint ?? "in MCP's verification ledger"}
            onRetry={retryOf(tenantsCount.state.kind, tenantsCount.reload)}
            detail="A count, not a rate — this route serves no history."
            from="—"
            to="now"
          />
        </div>

        <LabeebStrip
          businesses={labeebBusinesses.state}
          onBusinessesRetry={labeebBusinesses.reload}
          tiers={labeebTiers.state}
          onTiersRetry={labeebTiers.reload}
          configInfo={labeebConfig.state}
          onConfigRetry={labeebConfig.reload}
          env={labeebEnv.state}
          onEnvRetry={labeebEnv.reload}
        />

        {/* ── Tier distribution ───────────────────────────────────────── */}
        <div className="grid gap-4">
          {/* ONE panel, as the design draws it: the tier distribution and the
              credit posture are the same question — how the fleet is spread,
              and whether its ceilings are set — and a Divider between them
              says "still the same panel" where a second card said "a new
              subject". */}
          <TierAndCredits
            state={tierDistribution.state}
            onRetry={tierDistribution.reload}
            quota={quota.state}
            onQuotaRetry={quota.reload}
          />
        </div>

        {/* ── Decision queue · recent changes ─────────────────────────── */}
        <div className="grid gap-4 lg:grid-cols-2">
          <DecisionPanel tenants={tenants} businesses={labeebBusinesses} />
          <RecentChanges mcp={mcpAudit} labeeb={labeebAudit} />
        </div>

        {/* ── Top tools today · overrides per tier ─────────────────────── */}
        <div className="grid gap-4 lg:grid-cols-2">
          <TopToolsToday state={topToolsToday.state} onRetry={topToolsToday.reload} />
          <QuotaOverridesByTier
            state={quotaOverridesCount.state}
            onRetry={quotaOverridesCount.reload}
          />
        </div>
      </div>
    </Page>
  );
}
